import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import {
    Box,
    Button,
    Stack,
    Paper,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    CheckCircle as CheckCircleIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import styled from 'styled-components';

import { Container } from '@/components/common';
import { Input, ErrorMessage } from '@/components/common';
import { useJoinUser } from '@/hooks/users.hook';
import { successToast, errorToast } from '@/common/toast';
import { Controller } from 'react-hook-form';
import { users } from '@/constants';

/**
 * 테이블 헤더 스타일 컴포넌트
 */
const StyledTableHead = styled(TableHead)`
  background-color: #f8f9fa;
  
  .MuiTableCell-head {
    font-weight: 700;
    font-size: 0.875rem;
    color: #333;
    border-bottom: 2px solid #e0e0e0;
  }
`;

/**
 * 테이블 행 스타일 컴포넌트
 */
const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: #f5f5f5;
  }
  
  .MuiTableCell-root {
    padding: 12px 16px;
  }
`;

/**
 * 액션 버튼 컨테이너
 */
const ActionCell = styled(TableCell)`
  width: 80px;
  text-align: center;
`;

/**
 * 교육생 등록 페이지 컴포넌트
 * 
 * 표 형태로 여러 교육생을 한 번에 등록할 수 있는 페이지입니다.
 */
const StudentAddPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const joinUserMutation = useJoinUser();

    // URL 쿼리 파라미터에서 groupId 가져오기
    const groupIdFromQuery = searchParams.get('groupId');

    // 각 행의 등록 상태 관리 (index -> 등록 완료 여부)
    const [registeredRows, setRegisteredRows] = useState(new Set());
    // 각 행의 등록 중 상태 관리
    const [registeringRows, setRegisteringRows] = useState(new Set());
    // 각 행의 비밀번호 표시 상태 관리 (기본값: true - 비밀번호 보임)
    const [showPasswords, setShowPasswords] = useState(new Map());

    // 폼 관리
    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        reset,
    } = useForm({
        defaultValues: {
            users: [
                {
                    userId: '',
                    name: '',
                    email: '',
                    password: '',
                    groupId: groupIdFromQuery || '',
                    role: 'STUDENT',
                },
            ],
        },
    });

    // groupId 쿼리 파라미터가 변경되면 폼 초기값 업데이트
    useEffect(() => {
        if (groupIdFromQuery) {
            reset({
                users: [
                    {
                        userId: '',
                        name: '',
                        email: '',
                        password: '',
                        groupId: groupIdFromQuery,
                        role: 'STUDENT',
                    },
                ],
            });
        }
    }, [groupIdFromQuery, reset]);

    // 동적 배열 필드 관리
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'users',
    });

    /**
     * 행 추가 핸들러
     */
    const handleAddRow = useCallback(() => {
        append({
            userId: '',
            name: '',
            email: '',
            password: '',
            groupId: groupIdFromQuery || '',
            role: 'STUDENT',
        });
    }, [append, groupIdFromQuery]);

    /**
     * 행 삭제 핸들러
     */
    const handleRemoveRow = useCallback(
        (index) => {
            if (fields.length > 1) {
                // 등록된 행이면 삭제 불가
                if (registeredRows.has(index)) {
                    errorToast('이미 등록된 교육생은 삭제할 수 없습니다.');
                    return;
                }
                remove(index);
                // 등록 상태도 업데이트 (인덱스가 변경되므로)
                const newRegisteredRows = new Set();
                registeredRows.forEach((rowIndex) => {
                    if (rowIndex < index) {
                        newRegisteredRows.add(rowIndex);
                    } else if (rowIndex > index) {
                        newRegisteredRows.add(rowIndex - 1);
                    }
                });
                setRegisteredRows(newRegisteredRows);
            } else {
                errorToast('최소 1개의 행이 필요합니다.');
            }
        },
        [fields.length, remove, registeredRows]
    );

    /**
     * 특정 행의 교육생 등록 핸들러
     */
    const handleRegisterRow = useCallback(
        async (index) => {
            // 이미 등록된 행이면 등록 불가
            if (registeredRows.has(index)) {
                return;
            }

            // 등록 중인 행이면 중복 요청 방지
            if (registeringRows.has(index)) {
                return;
            }

            const formValues = getValues();
            const user = formValues.users[index];

            // 유효성 검사
            if (!user.userId || !user.name || !user.email || !user.password) {
                errorToast('모든 필수 항목을 입력해주세요.');
                return;
            }

            // 이메일 형식 검사
            const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            if (!emailPattern.test(user.email)) {
                errorToast('올바른 이메일 형식이 아닙니다.');
                return;
            }

            // 비밀번호 길이 검사
            if (user.password.length < 4) {
                errorToast('비밀번호는 최소 4자 이상이어야 합니다.');
                return;
            }

            // 등록 중 상태 설정
            setRegisteringRows((prev) => new Set(prev).add(index));

            try {
                await joinUserMutation.mutateAsync({
                    userId: user.userId,
                    name: user.name,
                    email: user.email,
                    password: user.password,
                    groupId: user.groupId ? Number(user.groupId) : undefined,
                    role: 'STUDENT',
                });

                // 등록 성공
                setRegisteredRows((prev) => new Set(prev).add(index));
                successToast(`${user.name}(${user.userId}) 교육생이 등록되었습니다.`);
            } catch (error) {
                errorToast(`교육생 등록에 실패했습니다. - ${error.response?.data?.msg}`);
            } finally {
                // 등록 중 상태 해제
                setRegisteringRows((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(index);
                    return newSet;
                });
            }
        },
        [registeredRows, registeringRows, joinUserMutation, getValues]
    );

    /**
     * 뒤로가기 핸들러
     */
    const handleGoBack = useCallback(() => {
        // groupId가 쿼리 파라미터에 있으면 그룹 사용자 목록으로, 없으면 일반 사용자 목록으로
        if (groupIdFromQuery) {
            navigate(`/groups/${groupIdFromQuery}/users`);
        } else {
            navigate('/students');
        }
    }, [navigate, groupIdFromQuery]);

    return (
        <Container title='교육생 등록'>
            <Stack spacing={3}>
                {/* 상단 버튼 영역 */}
                <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
                    <Button
                        variant='outlined'
                        onClick={handleGoBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        목록으로
                    </Button>
                    <Typography variant='body2' color='text.secondary'>
                        등록된 교육생: {registeredRows.size}명 / 전체: {fields.length}명
                    </Typography>
                </Box>

                {/* 안내 메시지 */}
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <PersonAddIcon sx={{ color: '#1976d2' }} />
                        <Typography variant='body2' color='text.secondary'>
                            각 행의 오른쪽 + 버튼을 클릭하여 해당 행의 교육생을 등록할 수 있습니다. 행 추가 버튼으로 새로운 행을 추가하세요.
                        </Typography>
                    </Stack>
                </Paper>

                {/* 교육생 등록 테이블 */}
                <Paper sx={{ overflow: 'auto' }}>
                    <TableContainer>
                        <Table stickyHeader>
                            <StyledTableHead>
                                <TableRow>
                                    <TableCell width='5%'>번호</TableCell>
                                    <TableCell width='15%'>
                                        사용자 ID <span style={{ color: 'red' }}>*</span>
                                    </TableCell>
                                    <TableCell width='15%'>
                                        이름 <span style={{ color: 'red' }}>*</span>
                                    </TableCell>
                                    <TableCell width='20%'>
                                        이메일 <span style={{ color: 'red' }}>*</span>
                                    </TableCell>
                                    <TableCell width='15%'>
                                        비밀번호 <span style={{ color: 'red' }}>*</span>
                                    </TableCell>
                                    <TableCell width='10%'>그룹 ID</TableCell>
                                    <ActionCell>작업</ActionCell>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {fields.map((field, index) => (
                                    <StyledTableRow key={field.id}>
                                        {/* 번호 */}
                                        <TableCell>{index + 1}</TableCell>

                                        {/* 사용자 ID */}
                                        <TableCell>
                                            <Input
                                                control={control}
                                                name={`users.${index}.userId`}
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    size: 'small',
                                                    placeholder: '사용자 ID',
                                                    variant: 'outlined',
                                                    disabled: registeredRows.has(index),
                                                    autoComplete: 'off',
                                                }}
                                                rules={{
                                                    required: '사용자 ID를 입력해주세요.',
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name={`users.${index}.userId`} />
                                        </TableCell>

                                        {/* 이름 */}
                                        <TableCell>
                                            <Input
                                                control={control}
                                                name={`users.${index}.name`}
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    size: 'small',
                                                    placeholder: '이름',
                                                    variant: 'outlined',
                                                    disabled: registeredRows.has(index),
                                                    autoComplete: 'off',
                                                }}
                                                rules={{
                                                    required: '이름을 입력해주세요.',
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name={`users.${index}.name`} />
                                        </TableCell>

                                        {/* 이메일 */}
                                        <TableCell>
                                            <Input
                                                control={control}
                                                name={`users.${index}.email`}
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    size: 'small',
                                                    type: 'email',
                                                    placeholder: '이메일',
                                                    variant: 'outlined',
                                                    disabled: registeredRows.has(index),
                                                    autoComplete: 'off',
                                                }}
                                                rules={{
                                                    required: '이메일을 입력해주세요.',
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: '올바른 이메일 형식이 아닙니다.',
                                                    },
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name={`users.${index}.email`} />
                                        </TableCell>

                                        {/* 비밀번호 */}
                                        <TableCell>
                                            <Input
                                                control={control}
                                                name={`users.${index}.password`}
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    size: 'small',
                                                    type: showPasswords.get(index) !== false ? 'text' : 'password',
                                                    placeholder: '비밀번호',
                                                    variant: 'outlined',
                                                    disabled: registeredRows.has(index),
                                                    autoComplete: 'new-password',
                                                    InputProps: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <IconButton
                                                                    aria-label='비밀번호 표시/숨김'
                                                                    onClick={() => {
                                                                        const newShowPasswords = new Map(showPasswords);
                                                                        newShowPasswords.set(index, !showPasswords.get(index));
                                                                        setShowPasswords(newShowPasswords);
                                                                    }}
                                                                    edge='end'
                                                                    disabled={registeredRows.has(index)}
                                                                >
                                                                    {showPasswords.get(index) !== false ? (
                                                                        <VisibilityIcon fontSize='small' />
                                                                    ) : (
                                                                        <VisibilityOffIcon fontSize='small' />
                                                                    )}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                                rules={{
                                                    required: '비밀번호를 입력해주세요.',
                                                    minLength: {
                                                        value: 4,
                                                        message: '비밀번호는 최소 4자 이상이어야 합니다.',
                                                    },
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name={`users.${index}.password`} />
                                        </TableCell>

                                        {/* 그룹 ID */}
                                        <TableCell>
                                            <Input
                                                control={control}
                                                name={`users.${index}.groupId`}
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    size: 'small',
                                                    type: 'number',
                                                    placeholder: '그룹 ID (선택)',
                                                    variant: 'outlined',
                                                    disabled: registeredRows.has(index) || Boolean(groupIdFromQuery),
                                                    autoComplete: 'off',
                                                }}
                                            />
                                        </TableCell>

                                        {/* 작업 버튼 */}
                                        <ActionCell>
                                            <Stack direction='row' spacing={0.5} justifyContent='center' alignItems='center'>
                                                {registeredRows.has(index) ? (
                                                    // 등록 완료된 행
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label='등록완료'
                                                        color='success'
                                                        size='small'
                                                        sx={{ height: 28, fontSize: '0.75rem' }}
                                                    />
                                                ) : (
                                                    // 등록 가능한 행
                                                    <>
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={() => handleRegisterRow(index)}
                                                            disabled={registeringRows.has(index)}
                                                            title='교육생 등록'
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: 'primary.lighter',
                                                                },
                                                            }}
                                                        >
                                                            {registeringRows.has(index) ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <AddIcon fontSize='small' />
                                                            )}
                                                        </IconButton>
                                                        <IconButton
                                                            size='small'
                                                            color='error'
                                                            onClick={() => handleRemoveRow(index)}
                                                            disabled={fields.length === 1 || registeredRows.has(index)}
                                                            title='행 삭제'
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: 'error.lighter',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize='small' />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Stack>
                                        </ActionCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 하단 안내 */}
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Typography variant='body2' color='text.secondary'>
                                총 {fields.length}개의 행이 있습니다.
                            </Typography>
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={<AddIcon />}
                                onClick={handleAddRow}
                            >
                                행 추가
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Stack>
        </Container>
    );
};

export default StudentAddPage;
